import React, { useState, useEffect, useCallback } from 'react';
import {
  GraduationCap, ArrowRight, ChevronDown, Loader2, AlertTriangle,
  Check, RotateCcw, History, RefreshCw, X, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const STATUS_BADGE = {
  false: { cls: 'bg-blue-100 text-blue-700', label: 'Active' },
  true:  { cls: 'bg-slate-100 text-slate-500', label: 'Rolled Back' },
};

export default function PromotionPage() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('promote'); // promote | history
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Promotion form state
  const [fromClass, setFromClass] = useState('');
  const [toClass, setToClass] = useState('');
  const [graduation, setGraduation] = useState(false);
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [excludeIds, setExcludeIds] = useState([]);
  const [executing, setExecuting] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  useEffect(() => {
    api.get('/promotions/classes')
      .then(r => { if (r.success) setClasses(r.data); })
      .catch(() => toast.error('Failed to load classes'))
      .finally(() => setLoading(false));
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/promotions/history');
      if (res.success) setHistory(res.data.promotions || []);
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  const handlePreview = async () => {
    if (!fromClass || (!toClass && !graduation)) {
      return toast.error('Select both source and target class');
    }
    setPreviewLoading(true);
    setExcludeIds([]);
    try {
      const toClassId = graduation ? fromClass : toClass; // graduation uses from as placeholder
      const res = await api.post('/promotions/preview', {
        from_class_id: fromClass,
        to_class_id: graduation ? fromClass : toClass,
        exclude_student_ids: [],
      });
      if (res.success) {
        if (graduation) {
          // Override for graduation display
          setPreview({ ...res.data, to_class: { name: 'Graduated (leave school)' } });
        } else {
          setPreview(res.data);
        }
      }
    } catch (err) { toast.error(err?.detail || 'Failed to preview'); }
    finally { setPreviewLoading(false); }
  };

  const toggleExclude = (id) => {
    setExcludeIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleExecute = async () => {
    if (!preview) return;
    const eligible = (preview.eligible_students || []).filter(s => !excludeIds.includes(s.id));
    if (eligible.length === 0) return toast.error('No students selected for promotion');

    if (!window.confirm(`Promote ${eligible.length} student(s) from ${preview.from_class?.name} to ${graduation ? 'Graduated' : preview.to_class?.name}? This can be rolled back.`)) return;

    setExecuting(true);
    try {
      const res = await api.post('/promotions/execute', {
        from_class_id: fromClass,
        to_class_id: graduation ? fromClass : toClass,
        exclude_student_ids: excludeIds,
        graduation,
        note,
      });
      if (res.success) {
        toast.success(res.data.message);
        setPreview(null);
        setFromClass('');
        setToClass('');
        setNote('');
        setExcludeIds([]);
        setGraduation(false);
        setTab('history');
        loadHistory();
        // Refresh classes
        api.get('/promotions/classes').then(r => { if (r.success) setClasses(r.data); });
      }
    } catch (err) { toast.error(err?.detail || 'Promotion failed'); }
    finally { setExecuting(false); }
  };

  const handleRollback = async (promoId) => {
    if (!window.confirm('Roll back this promotion? Students will be moved back to their previous class.')) return;
    setRollingBack(promoId);
    try {
      const res = await api.post(`/promotions/${promoId}/rollback`, {});
      if (res.success) {
        toast.success(res.message);
        loadHistory();
        api.get('/promotions/classes').then(r => { if (r.success) setClasses(r.data); });
      }
    } catch (err) { toast.error(err?.detail || 'Rollback failed'); }
    finally { setRollingBack(null); }
  };

  const fromClassObj = classes.find(c => c.id === fromClass);
  const toClassObj = classes.find(c => c.id === toClass);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="promotion-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-blue-600" />
          Promotion Management
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Promote students between classes at end of term/year — with full rollback support
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-5 w-fit">
        {[['promote', 'Promote Students'], ['history', 'Promotion History']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === key ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Promote Students Tab */}
      {tab === 'promote' && (
        <div className="space-y-5">
          {/* Class Selector */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="font-semibold text-slate-800 mb-4">Configure Promotion</h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
              {/* From class */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">From Class</label>
                <div className="relative">
                  <select
                    value={fromClass}
                    onChange={e => { setFromClass(e.target.value); setPreview(null); setExcludeIds([]); }}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none appearance-none pr-7"
                    data-testid="from-class-select"
                    disabled={loading}
                  >
                    <option value="">Select class...</option>
                    {classes.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.student_count} students)</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Arrow */}
              <div className="flex flex-col items-center gap-2 pt-4">
                <ArrowRight className="w-6 h-6 text-slate-300" />
                <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={graduation}
                    onChange={e => { setGraduation(e.target.checked); setToClass(''); setPreview(null); }}
                    className="w-3.5 h-3.5 accent-blue-600"
                    data-testid="graduation-checkbox"
                  />
                  Graduation (exit school)
                </label>
              </div>

              {/* To class */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">To Class</label>
                {graduation ? (
                  <div className="w-full border border-slate-200 bg-slate-50 rounded-lg px-3 py-2.5 text-sm text-slate-400">
                    Graduated (leave school)
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={toClass}
                      onChange={e => { setToClass(e.target.value); setPreview(null); setExcludeIds([]); }}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none appearance-none pr-7"
                      data-testid="to-class-select"
                      disabled={!fromClass}
                    >
                      <option value="">Select target class...</option>
                      {classes.filter(c => c.id !== fromClass).map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.student_count} students)</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>
                )}
              </div>
            </div>

            {/* Note */}
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase">Note (optional)</label>
              <input
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="e.g. End of 2024/2025 session"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
                data-testid="promotion-note-input"
              />
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={handlePreview}
                disabled={!fromClass || (!toClass && !graduation) || previewLoading}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                data-testid="preview-promotion-btn"
              >
                {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Preview Promotion
              </button>
            </div>
          </div>

          {/* Preview results */}
          {preview && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="promotion-preview">
              {/* Summary */}
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2">
                  <span className="font-semibold text-blue-800 text-sm">{preview.from_class?.name}</span>
                  <ArrowRight className="w-4 h-4 text-blue-400" />
                  <span className="font-semibold text-blue-800 text-sm">{preview.to_class?.name || (graduation ? 'Graduated' : '?')}</span>
                </div>
                <div className="flex gap-3 ml-auto text-sm">
                  <span className="text-slate-500">
                    <span className="font-bold text-slate-800">{preview.eligible_students?.length - excludeIds.length}</span> will be promoted
                  </span>
                  {excludeIds.length > 0 && (
                    <span className="text-orange-500">
                      <span className="font-bold">{excludeIds.length}</span> excluded
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase">
                  Students — uncheck to exclude from promotion
                </p>
                <button
                  onClick={() => setExcludeIds(excludeIds.length === 0 ? [] : [])}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  {excludeIds.length > 0 ? 'Clear exclusions' : ''}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto mb-4">
                {(preview.eligible_students || []).map(s => {
                  const excluded = excludeIds.includes(s.id);
                  return (
                    <div
                      key={s.id}
                      onClick={() => toggleExclude(s.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                        excluded ? 'bg-red-50 border-red-200 opacity-60' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                      data-testid="student-promotion-row"
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                        excluded ? 'bg-red-200' : 'bg-blue-600'
                      }`}>
                        {excluded ? <X className="w-3 h-3 text-red-600" /> : <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{s.first_name} {s.last_name}</p>
                        <p className="text-xs text-slate-400">{s.admission_number}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setPreview(null); setExcludeIds([]); }}
                  className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExecute}
                  disabled={executing || (preview.eligible_students?.length - excludeIds.length) === 0}
                  className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition disabled:opacity-60"
                  data-testid="execute-promotion-btn"
                >
                  {executing && <Loader2 className="w-4 h-4 animate-spin" />}
                  <GraduationCap className="w-4 h-4" />
                  Confirm Promotion ({(preview.eligible_students?.length || 0) - excludeIds.length} students)
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {historyLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-16">
              <History className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No promotions recorded yet</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">Date</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">From → To</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-500 text-xs uppercase">Students</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">By</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-500 text-xs uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {history.map(p => (
                  <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid="promotion-history-row">
                    <td className="px-4 py-3 text-xs text-slate-500">{(p.created_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-slate-700">{p.from_class_name}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                        <span className="font-medium text-slate-700">{p.graduation ? 'Graduated' : p.to_class_name}</span>
                      </div>
                      {p.note && <p className="text-xs text-slate-400 mt-0.5">{p.note}</p>}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-slate-700">{p.student_count}</td>
                    <td className="px-4 py-3 text-slate-600">{p.promoted_by_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[p.rolled_back].cls}`}>
                        {STATUS_BADGE[p.rolled_back].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!p.rolled_back && !p.graduation && (
                        <button
                          onClick={() => handleRollback(p.id)}
                          disabled={rollingBack === p.id}
                          className="flex items-center gap-1 text-xs text-orange-600 border border-orange-200 px-2.5 py-1.5 rounded-lg hover:bg-orange-50 ml-auto disabled:opacity-60"
                          data-testid="rollback-btn"
                        >
                          {rollingBack === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                          Rollback
                        </button>
                      )}
                      {p.rolled_back && <span className="text-xs text-slate-400">Rolled back {(p.rolled_back_at || '').slice(0, 10)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
